import {
  DepartmentMemberMultiPicker as CoreDepartmentMemberMultiPicker,
  DepartmentMemberPicker as CoreDepartmentMemberPicker,
  DepartmentMultiPicker as CoreDepartmentMultiPicker,
  DepartmentPicker as CoreDepartmentPicker,
  MemberMultiPicker as CoreMemberMultiPicker,
  MemberPicker as CoreMemberPicker,
} from '@zhiyue/core';
import { Spin } from 'antd';
import React from 'react';
import { useOpenPlatformToken } from '@/hooks/useOpenPlatformToken';
import { getAccessToken } from '@/services/oauth2';

export const usePickerToken = () => {
  const { token: openPlatformToken, loading } = useOpenPlatformToken();
  const fallbackToken = React.useMemo(() => getAccessToken() || '', []);
  const token = openPlatformToken || fallbackToken;
  return { token, loading: loading && !token };
};

export type SinglePickerProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

export type MultiPickerProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  modalTitle?: string;
};

/** 单选成员选择器（Form.Item 受控） */
export const MemberPickerField: React.FC<SinglePickerProps> = ({
  value,
  onChange,
  placeholder = '选择成员',
  disabled,
  className,
  style,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreMemberPicker
        token={token}
        value={value || null}
        onChange={(next) => onChange?.(next)}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

/** 多选成员选择器（Form.Item 受控） */
export const MemberMultiPickerField: React.FC<MultiPickerProps> = ({
  value = [],
  onChange,
  placeholder = '选择成员',
  disabled,
  className,
  style,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreMemberMultiPicker
        token={token}
        value={value || []}
        onChange={(next) => onChange?.(next || [])}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

/** 单选部门选择器（Form.Item 受控） */
export const DepartmentPickerField: React.FC<SinglePickerProps> = ({
  value,
  onChange,
  placeholder = '选择部门',
  disabled,
  className,
  style,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreDepartmentPicker
        token={token}
        value={value || null}
        onChange={(next) => onChange?.(next)}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

/** 多选部门选择器（Form.Item 受控） */
export const DepartmentMultiPickerField: React.FC<MultiPickerProps> = ({
  value = [],
  onChange,
  placeholder = '选择部门',
  disabled,
  className,
  style,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreDepartmentMultiPicker
        token={token}
        value={value || []}
        onChange={(next) => onChange?.(next || [])}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

/** 部门与成员单选选择器（Form.Item 受控） */
export const DepartmentMemberPickerField: React.FC<SinglePickerProps & { modalTitle?: string }> = ({
  value,
  onChange,
  placeholder = '选择部门或成员',
  disabled,
  className,
  style,
  modalTitle,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreDepartmentMemberPicker
        token={token}
        value={value || null}
        onChange={(next) => onChange?.(next)}
        placeholder={placeholder}
        disabled={disabled}
        modalTitle={modalTitle}
      />
    </div>
  );
};

/** 部门与成员多选选择器（Form.Item 受控） */
export const DepartmentMemberMultiPickerField: React.FC<MultiPickerProps> = ({
  value = [],
  onChange,
  placeholder = '选择部门或成员',
  disabled,
  className,
  style,
  modalTitle,
}) => {
  const { token, loading } = usePickerToken();

  if (loading && !token) {
    return <Spin size="small" />;
  }

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <CoreDepartmentMemberMultiPicker
        token={token}
        value={value || []}
        onChange={(next) => onChange?.(next || [])}
        placeholder={placeholder}
        disabled={disabled}
        modalTitle={modalTitle}
      />
    </div>
  );
};
